import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

export interface UserPreferences {
  city: string;
}

const USER_PREFERENCES_KEY = 'user-preferences';

@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {
  private _preferences = signal<UserPreferences>({ city: '' });
  
  // Expose as observable for components to subscribe
  public preferences$ = toObservable(this._preferences);
  
  constructor() {
    this.loadPreferences();
  }
  
  /**
   * Check if user has already selected a city
   */
  hasCity(): boolean {
    return !!this._preferences().city;
  }
  
  /**
   * Get the currently selected city
   */
  getCity(): string {
    return this._preferences().city;
  }
  
  /**
   * Set user's city preference and save to localStorage
   */
  setCity(city: string): void {
    this._preferences.update(prefs => ({ ...prefs, city }));
    this.savePreferences();
  }
  
  /**
   * Reset city preference to force re-prompting
   */
  resetCity(): void {
    this._preferences.update(prefs => ({ ...prefs, city: '' }));
    this.savePreferences();
  }
  
  /**
   * Load user preferences from localStorage
   */
  private loadPreferences(): void {
    try {
      const savedPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
      if (savedPreferences) {
        const parsedPreferences = JSON.parse(savedPreferences) as UserPreferences;
        this._preferences.set(parsedPreferences);
      }
    } catch (error) {
      console.error('Failed to load user preferences', error);
    }
  }
  
  /**
   * Save current preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(this._preferences()));
    } catch (error) {
      console.error('Failed to save user preferences', error);
    }
  }
} 